<template>
  <Teleport to="body">
    <div v-if="modelValue" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ title }}</h3>
          <button type="button" class="modal-close" aria-label="Close" @click="handleClose">
            ×
          </button>
        </div>
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <div class="row">
              <div style="flex: 1">
                <label>{{ s__('TaskForm|Title') }} *</label>
                <input
                  v-model="form.title"
                  type="text"
                  maxlength="32"
                  :placeholder="s__('TaskForm|e.g., Bug, Feature, Urgent')"
                  @keydown.enter.prevent="handleSubmit"
                />
              </div>
              <div style="flex: 0 0 150px">
                <label>{{ s__('TaskForm|Color') }} *</label>
                <input v-model="form.color" type="color" />
              </div>
            </div>
            <div
              class="mobile-stretch-btn-group"
              style="display: flex; gap: 8px; margin-top: 16px; justify-content: flex-end"
            >
              <button type="button" class="btn-secondary" @click="handleClose">
                {{ __('Cancel') }}
              </button>
              <button type="submit" :disabled="!canSubmit || creating" class="btn-primary">
                {{ creating ? creatingLabel : submitLabel }}
              </button>
            </div>
            <div v-if="error" class="error" style="margin-top: 12px">{{ error }}</div>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { s__, __ } from '@/locale'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  creating: {
    type: Boolean,
    default: false,
  },
  submitLabel: {
    type: String,
    default: () => s__('TaskForm|Create'),
  },
  creatingLabel: {
    type: String,
    default: () => s__('TaskForm|Creating...'),
  },
  initialTitle: {
    type: String,
    default: '',
  },
  initialColor: {
    type: String,
    default: '#2196F3',
  },
  error: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'submit', 'close'])

const form = ref({
  title: props.initialTitle,
  color: props.initialColor,
})

const canSubmit = computed(() => {
  return form.value.title.trim().length > 0 && form.value.color
})

watch(
  () => props.modelValue,
  isOpen => {
    if (isOpen) {
      form.value = {
        title: props.initialTitle || '',
        color: props.initialColor || '#2196F3',
      }
      return
    }

    // Reset form when modal closes
    form.value = {
      title: props.initialTitle || '',
      color: props.initialColor || '#2196F3',
    }
  }
)

function handleClose(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  emit('update:modelValue', false)
  emit('close')
}

function handleSubmit(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  if (!canSubmit.value) return
  emit('submit', {
    title: form.value.title.trim(),
    color: form.value.color,
  })
}
</script>

<style scoped>
/* Modal styles */
/* Component-specific: z-index and padding override */
.modal-overlay {
  z-index: 10000;
  padding: 20px;
}

/* Component-specific: max-width override */
.modal-content {
  max-width: 500px;
}

.modal-body .row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
}

.modal-body label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
}

.modal-body input[type='text'] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 16px;
  height: 38px;
  min-height: 38px;
  box-sizing: border-box;
}

.modal-body input[type='color'] {
  width: 100%;
  height: 38px;
  min-height: 38px;
  padding: 2px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .modal-body input[type='text'],
  .modal-body input[type='color'] {
    font-size: 16px;
    height: 44px;
    min-height: 44px;
  }
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.error {
  color: #c62828;
  background: #ffebee;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ef5350;
}
</style>
